// Centralized design tokens for CostGuard
// All colors, spacing, typography, and component styles should reference these

export const colors = {
  // Backgrounds
  bg: "#020617",
  bgMid: "#060e24",
  cardBg: "rgba(255, 255, 255, 0.04)",
  cardBgHover: "rgba(255, 255, 255, 0.07)",
  surface: "rgba(6, 14, 36, 0.94)",
  surfaceSoft: "rgba(6, 14, 36, 0.75)",
  surfaceSubtle: "rgba(6, 14, 36, 0.3)",

  // Text
  text: "#e5e7eb",
  textMuted: "#94a3b8",
  textSecondary: "#94a3b8",
  textPrimary: "#e5e7eb",
  textStrong: "#f1f5f9",
  textLight: "#e2e8f0",
  textSoft: "#cbd5e1",
  textTertiary: "#64748b",
  textFaint: "#475569",
  textDisabled: "#334155",
  textBright: "#f8fafc",
  textOnAccent: "#000",
  white: "#fff",

  // Primary accent — sky blue
  green: "#38bdf8",
  greenRgb: "56, 189, 248",
  greenSubtle: "rgba(56, 189, 248, 0.12)",
  greenBorder: "rgba(56, 189, 248, 0.25)",
  greenBorderStrong: "rgba(56, 189, 248, 0.4)",
  greenGlow: "rgba(56, 189, 248, 0.3)",

  // Semantic status colours (intentional — not brand)
  skyBlue: "#38bdf8",           // Cash runway, positive cash signals
  skyBlueSubtle: "rgba(56, 189, 248, 0.15)",
  skyBlueBorder: "rgba(56, 189, 248, 0.2)",
  amber: "#fbbf24",             // Tax obligations, warnings
  amberSubtle: "rgba(251, 191, 36, 0.08)",
  amberBorder: "rgba(251, 191, 36, 0.2)",
  amberDark: "#f59e0b",
  warningText: "#fde047",
  warningBorder: "rgba(251, 191, 36, 0.3)",
  alert: "#f87171",             // Errors, destructive actions
  danger: "#f87171",
  alertSubtle: "rgba(239, 68, 68, 0.08)",
  alertBorder: "rgba(239, 68, 68, 0.3)",
  dangerText: "#fca5a5",
  dangerBorder: "rgba(248, 113, 113, 0.3)",
  success: "#22c55e",           // Green stays for confirmed/saved states
  successText: "#86efac",
  successMedium: "#4ade80",
  successPale: "#a7f3d0",
  successDark: "#166534",
  successBorder: "rgba(34, 197, 94, 0.3)",
  blue: "#3b82f6",
  infoText: "#93c5fd",
  skyBlueText: "#7dd3fc",

  // Borders
  border: "rgba(255, 255, 255, 0.08)",
  borderStrong: "rgba(255, 255, 255, 0.15)",
  borderSubtle: "rgba(255, 255, 255, 0.05)",
  borderInput: "rgba(255, 255, 255, 0.12)",
  slateBorder: "#1e293b",

  // Interactive
  hoverBg: "rgba(255, 255, 255, 0.06)",
  disabled: "rgba(156, 163, 175, 0.4)",
  progressTrackBg: "rgba(148, 163, 184, 0.12)",
  focusRing: "rgba(56, 189, 248, 0.7)",
  activeText: "#10B981",
  activeBg: "rgba(16, 185, 129, 0.12)",
  activeBorder: "#059669",

  // Overlays
  backdrop: "rgba(2, 6, 23, 0.8)",
  inputBg: "#0a1628",
  modalBg: "#0f172a",
  modalBgStrong: "rgba(15, 23, 42, 0.98)",
  tooltipBg: "rgba(15, 23, 42, 0.95)",
  bgDeep: "#060f20",
  phoneShellBg: "rgba(6, 15, 36, 0.7)",
  emeraldOverlay: "rgba(30, 111, 92, 0.9)",

  // Common opacity scales used by existing components
  white3: "rgba(255, 255, 255, 0.03)",
  white4: "rgba(255, 255, 255, 0.04)",
  white5: "rgba(255, 255, 255, 0.05)",
  white6: "rgba(255, 255, 255, 0.06)",
  white8: "rgba(255, 255, 255, 0.08)",
  white10: "rgba(255, 255, 255, 0.1)",
  white12: "rgba(255, 255, 255, 0.12)",
  white15: "rgba(255, 255, 255, 0.15)",
  white20: "rgba(255, 255, 255, 0.2)",
  black10: "rgba(0, 0, 0, 0.1)",
  black15: "rgba(0, 0, 0, 0.15)",
  black20: "rgba(0, 0, 0, 0.2)",
  black40: "rgba(0, 0, 0, 0.4)",
  black60: "rgba(0, 0, 0, 0.6)",
  slate6: "rgba(148, 163, 184, 0.06)",
  slate8: "rgba(148, 163, 184, 0.08)",
  slate10: "rgba(148, 163, 184, 0.1)",
  slate15: "rgba(148, 163, 184, 0.15)",
  slate20: "rgba(148, 163, 184, 0.2)",
  slate25: "rgba(148, 163, 184, 0.25)",
  slate30: "rgba(148, 163, 184, 0.3)",
  slate35: "rgba(148, 163, 184, 0.35)",
  slate50: "rgba(148, 163, 184, 0.5)",
  slate60: "rgba(148, 163, 184, 0.6)",
  skyBlue3: "rgba(56, 189, 248, 0.03)",
  skyBlue4: "rgba(56, 189, 248, 0.04)",
  skyBlue6: "rgba(56, 189, 248, 0.06)",
  skyBlue7: "rgba(56, 189, 248, 0.07)",
  skyBlue8: "rgba(56, 189, 248, 0.08)",
  skyBlue10: "rgba(56, 189, 248, 0.1)",
  skyBlue12: "rgba(56, 189, 248, 0.12)",
  skyBlue15: "rgba(56, 189, 248, 0.15)",
  skyBlue20: "rgba(56, 189, 248, 0.2)",
  skyBlue25: "rgba(56, 189, 248, 0.25)",
  skyBlue30: "rgba(56, 189, 248, 0.3)",
  skyBlue45: "rgba(56, 189, 248, 0.45)",
  skyBlue55: "rgba(56, 189, 248, 0.55)",
  success6: "rgba(34, 197, 94, 0.06)",
  success8: "rgba(34, 197, 94, 0.08)",
  success10: "rgba(34, 197, 94, 0.1)",
  success12: "rgba(34, 197, 94, 0.12)",
  success15: "rgba(34, 197, 94, 0.15)",
  success20: "rgba(34, 197, 94, 0.2)",
  success25: "rgba(34, 197, 94, 0.25)",
  success30: "rgba(34, 197, 94, 0.3)",
  success40: "rgba(34, 197, 94, 0.4)",
  success50: "rgba(34, 197, 94, 0.5)",
  successMedium60: "rgba(74, 222, 128, 0.6)",
  alert6: "rgba(239, 68, 68, 0.06)",
  alert10: "rgba(239, 68, 68, 0.1)",
  alert20: "rgba(239, 68, 68, 0.2)",
  alert30: "rgba(239, 68, 68, 0.3)",
  amber6: "rgba(251, 191, 36, 0.06)",
  amber10: "rgba(251, 191, 36, 0.1)",
  amber12: "rgba(251, 191, 36, 0.12)",
  amber15: "rgba(251, 191, 36, 0.15)",
  amber20: "rgba(251, 191, 36, 0.2)",
  amber25: "rgba(251, 191, 36, 0.25)",
  amber30: "rgba(251, 191, 36, 0.3)",
  amber40: "rgba(251, 191, 36, 0.4)",
  amberDark20: "rgba(245, 158, 11, 0.2)",
  amberDark40: "rgba(245, 158, 11, 0.4)",
  blue15: "rgba(59, 130, 246, 0.15)",
  blue20: "rgba(59, 130, 246, 0.2)",
  blue30: "rgba(59, 130, 246, 0.3)",
  blue40: "rgba(59, 130, 246, 0.4)",
  textOverlay: "rgba(229, 231, 235, 0.85)",
  successText10: "rgba(134, 239, 172, 0.1)",
  dangerText10: "rgba(252, 165, 165, 0.1)",
} as const

export const spacing = {
  xs: "4px",
  sm: "8px",
  md: "12px",
  lg: "16px",
  xl: "24px",
  xxl: "32px",
  "3xl": "48px",
} as const

export const breakpoints = {
  mobile: 768,
  tablet: 1024,
} as const;

export const layout = {
  content: 980,
} as const;

export const typography = {
  // Font sizes
  fontSize: {
    xs: "11px",
    sm: "13px",
    base: "14px",
    md: "16px",
    lg: "18px",
    xl: "22px",
    "2xl": "28px",
    // Mobile (viewport < 768px): use for body/secondary on narrow viewports
    baseMobile: "16px",
    smMobile: "14px",
  },
  // Font weights
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  // Line heights
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.6,
  },
} as const;

/** Mobile + desktop type scale (Apple HIG aligned). Use via useMobileType() for breakpoint-aware sizing. */
export const typeScale = {
  mobile: {
    title: 24,       // Page headings
    heading: 18,    // Section headings
    cardTitle: 16,  // Card headers
    body: 16,       // Primary text, values
    secondary: 14,   // Labels, descriptions
    tertiary: 12,   // Timestamps, metadata
    micro: 11,      // Badges, chips only
    display: 22,    // Dollar amounts
  },
  desktop: {
    title: 28,
    heading: 20,
    cardTitle: 16,
    body: 14,
    secondary: 13,
    tertiary: 11,
    micro: 10,
    display: 20,
  },
} as const

export const borderRadius = {
  sm: "6px",
  md: "8px",
  lg: "10px",
  xl: "14px",
  pill: "999px",
} as const

export const shadows = {
  card: "0 18px 40px rgba(0, 0, 0, 0.7)",
  cardHover: "0 18px 50px rgba(0, 0, 0, 0.75)",
  button: "0 10px 24px rgba(0, 0, 0, 0.5)",
  buttonActive: "0 4px 12px rgba(0, 0, 0, 0.7)",
  focus: "0 0 0 1px rgba(56, 189, 248, 0.7), 0 0 0 9px rgba(56, 189, 248, 0.07)",
  drawer: "0 0 40px rgba(0, 0, 0, 0.6)",
} as const

export const transitions = {
  fast: "0.08s ease",
  normal: "0.15s ease",
  smooth: "0.25s cubic-bezier(0.22, 0.61, 0.36, 1)",
  drawer: "0.2s ease",
} as const

// Card glow by type — sky blue accent
export const cardGlow = {
  dock: "0 0 12px rgba(56, 189, 248, 0.15)",
  console: "0 0 8px rgba(56, 189, 248, 0.08)",
  module: "0 0 4px rgba(56, 189, 248, 0.04)",
} as const
